/**
 * Marketplace Constants
 *
 * Category definitions, filter metadata, and default configurations
 * for the Marketplace feature.
 */

/**
 * Order categories for DrayNow host
 */
export const DRAYNOW_ORDER_CATEGORIES = [
  'open',
  'pending',
  'tendered',
  'in_progress',
  'finished',
  'closed',
  'canceled',
]

/**
 * Order categories for Modal View host
 */
export const MODAL_VIEW_ORDER_CATEGORIES = [
  'alerts',
  'drafts',
  'pending_carrier',
  'tendered',
  'in_progress',
  'finished',
  'closed',
  'canceled',
]

/**
 * Overview categories for DrayNow
 */
export const DRAYNOW_OVERVIEW_CATEGORIES = [
  'open',
  'pending',
  'tendered',
  'in_progress',
  'finished',
]

/**
 * Overview categories for Modal View
 *
 * `pending_carrier` (Tender Pending) is the ModalView counterpart of DrayNow's
 * pre-acceptance `open` / `pending` categories, so it belongs in the overview
 * and is selected by default like every other entry (MV-3398 QA follow-up).
 */
export const MODAL_VIEW_OVERVIEW_CATEGORIES = [
  'pending_carrier',
  'tendered',
  'in_progress',
  'finished',
]

/**
 * Filter metadata for DrayNow host
 *
 * Note: category visibility is deliberately NOT gated on the
 * `canViewOpenMarketplace` permission — legacy production never enforced it
 * in the sidebar (its `displayPredicate` was dead code), so the sidebar shows
 * every category returned by the counts API. Enforcing the company-level
 * toggle here is a product decision, not a porting gap.
 */
export const DRAYNOW_FILTER_META = {
  overview: { icon: '', title: 'Overview' },
  search: { icon: '', title: 'Search' },
  alerts: { icon: 'warning circle', title: 'Alerts' },
  drafts: { icon: 'pencil', title: 'Drafts' },
  open: {
    icon: 'inbox',
    title: 'Tendered to DrayNow',
  },
  pending: {
    icon: 'hourglass half',
    title: 'Pending',
  },
  tendered: { icon: 'check circle outline', title: 'Driver Assigned' },
  in_progress: { icon: 'truck', title: 'In Progress' },
  finished: { icon: 'flag checkered', title: 'Finished' },
  closed: { icon: 'folder', title: 'Closed' },
  canceled: { icon: 'remove', title: 'Canceled' },
}

/**
 * Filter metadata for Modal View host
 */
export const MODAL_VIEW_FILTER_META = {
  overview: { icon: '', title: 'Overview' },
  search: { icon: '', title: 'Search' },
  alerts: { icon: 'warning circle', title: 'Alerts' },
  drafts: { icon: 'pencil', title: 'Draft Tender' },
  pending_carrier: {
    icon: 'hourglass half',
    title: 'Tender Pending',
  },
  tendered: { icon: 'check circle outline', title: 'Tendered' },
  in_progress: { icon: 'truck', title: 'In Progress' },
  finished: { icon: 'flag checkered', title: 'Finished' },
  closed: { icon: 'folder', title: 'Closed' },
  canceled: { icon: 'remove', title: 'Canceled' },
}

/**
 * Default per-page count
 */
export const DEFAULT_PER_PAGE = 25

/**
 * Default sort configuration for orders
 */
export const DEFAULT_ORDERS_SORT = {
  field: 'firstAppointmentEnd',
  direction: 'asc',
}

/**
 * Default sort configuration for trips
 */
export const DEFAULT_TRIPS_SORT = {
  field: 'appointmentStart',
  direction: 'asc',
}

/**
 * List types
 */
export const LIST_TYPES = {
  ORDERS: 'orders',
  TRIPS: 'trips',
}

/**
 * Per-category default sort, ported from legacy `Marketplace.defaultSort`
 * (src/app/models/Marketplace.js). The default sort varies by category + list
 * type, and for `overview` by theme:
 * - closed/canceled → DESC (newest first)
 * - overview on DrayNow → multi-key (alert severity → category order →
 *   appointment), verbatim legacy
 * - overview on ModalView → multi-key (category order → appointment). The
 *   ModalView Order Overview (MV-3398) has no Alerts column, so leading with
 *   alert severity would order rows by a value the user cannot see and leave
 *   no header carrying the sort caret. Category is the first visible column
 *   whose `sortKey` (categoryOrder) can own the default.
 * - everything else → ASC single-key (the DEFAULT_*_SORT above)
 *
 * Returns the backend sort shape: an array of `{ field: 'ASC'|'DESC' }`.
 * @param {string} category - Current category (filter) key
 * @param {string} listType - LIST_TYPES.ORDERS or LIST_TYPES.TRIPS
 * @param {boolean} [isDrayNow=false] - Whether the host is DrayNow
 * @returns {Array<Object>} Sort array
 */
export const getDefaultSort = (category, listType, isDrayNow = false) => {
  const isTrips = listType === LIST_TYPES.TRIPS
  if (category === 'closed' || category === 'canceled') {
    return [{ [isTrips ? 'appointmentStart' : 'firstAppointmentEnd']: 'DESC' }]
  }
  if (category === 'overview') {
    return isDrayNow
      ? [
          { alertSeverityOrder: 'ASC' },
          { categoryOrder: 'ASC' },
          { firstAppointmentEnd: 'ASC' },
        ]
      : [{ categoryOrder: 'ASC' }, { firstAppointmentEnd: 'ASC' }]
  }
  const base = isTrips ? DEFAULT_TRIPS_SORT : DEFAULT_ORDERS_SORT
  return [{ [base.field]: base.direction.toUpperCase() }]
}

/**
 * Single-key `{ field, direction }` derived from the first key of the
 * category's default sort — drives the column sort indicator and the URL
 * default-detection in useMarketplaceParams (mirrors legacy `appliedSort[0]`).
 * @param {string} category - Current category (filter) key
 * @param {string} listType - LIST_TYPES.ORDERS or LIST_TYPES.TRIPS
 * @param {boolean} [isDrayNow=false] - Whether the host is DrayNow
 * @returns {{ field: string, direction: string }} Single-key sort state
 */
export const getDefaultSortState = (category, listType, isDrayNow = false) => {
  const [first] = getDefaultSort(category, listType, isDrayNow)
  const field = Object.keys(first)[0]
  return { field, direction: String(first[field]).toLowerCase() }
}

/**
 * Sort keys that expand to a multi-key backend sort. The map key is the
 * column's `sortKey` (the primary key — what lands in the `?sort=` URL param
 * and drives the caret); the value is the full ordered key list sent to the
 * API. Legacy parity (marketplaceTableMap.modalViewTripSorts): `driver.lastName`
 * breaks ties on identical first names, and the trailing `uid` is a unique
 * tiebreaker so offset pagination stays stable across pages. Legacy flipped
 * every key's direction together, so one direction applies to all keys.
 */
export const MULTI_KEY_SORTS = {
  'driver.firstName': ['driver.firstName', 'driver.lastName', 'uid'],
  driverCompletedTrip: ['driverCompletedTrip', 'uid'],
}

/**
 * Build the backend sort array for an explicit (user-clicked) column sort.
 * Fields listed in MULTI_KEY_SORTS expand to their full key list; everything
 * else stays single-key.
 * @param {string} field - Primary sort field (the column's sortKey)
 * @param {string} direction - 'asc' or 'desc' (case-insensitive)
 * @returns {Array<Object>} Sort array of `{ field: 'ASC'|'DESC' }`
 */
export const buildExplicitSort = (field, direction) => {
  const dir = String(direction).toUpperCase()
  return (MULTI_KEY_SORTS[field] || [field]).map((key) => ({ [key]: dir }))
}

/**
 * localStorage keys
 */
export const STORAGE_KEYS = {
  ADMIN_FILTERS: 'marketplace_adminFilters',
}

/**
 * Base polling interval in milliseconds (3 minutes) for every polled query.
 *
 * This is the interval while the backend is healthy. `useApiQuery`'s `poll`
 * option backs it off exponentially while a query keeps failing, and a hidden
 * tab does not poll at all, so the real request rate is at or below this.
 */
export const POLLING_INTERVAL = 180000

/**
 * Query keys every marketplace-changing mutation must invalidate.
 * Legacy parity: `marketplace.refresh()` always refetched the list AND the
 * category counts after every action. String keys prefix-match all param
 * variants (see useApiMutation invalidateKeys).
 */
export const MARKETPLACE_INVALIDATIONS = [
  'getMarketplaceOrders',
  'getMarketplaceTrips',
  'getMarketplaceOrdersCounts',
  'getMarketplaceTripsCounts',
  // The details PAGE and TripCell's details modal both read their entity from
  // this query rather than from a list row, so without it an action taken on
  // either left the entity stale for its full staleTime. TripCell's query is
  // kept enabled while its modal is open (TripCell.logic.js) precisely so this
  // invalidation can reach it — refetchType defaults to 'active', and a
  // disabled observer is not active.
  'getTripByUid',
]

/**
 * Status colors for UI display
 */
export const STATUS_COLORS = {
  open: 'blue',
  pending: 'yellow',
  tendered: 'orange',
  in_progress: 'teal',
  finished: 'green',
  closed: 'grey',
  canceled: 'red',
  alerts: 'red',
  drafts: 'grey',
  pending_carrier: 'yellow',
}

/**
 * Get order categories based on host type
 * @param {boolean} isDrayNow - Whether the host is DrayNow
 * @returns {string[]} Array of category keys
 */
export const getOrderCategories = (isDrayNow = false) => {
  return isDrayNow ? DRAYNOW_ORDER_CATEGORIES : MODAL_VIEW_ORDER_CATEGORIES
}

/**
 * Get filter metadata based on host type
 * @param {boolean} isDrayNow - Whether the host is DrayNow
 * @returns {Object} Filter metadata object
 */
export const getFilterMeta = (isDrayNow = false) => {
  return isDrayNow ? DRAYNOW_FILTER_META : MODAL_VIEW_FILTER_META
}

/**
 * Get overview categories based on host type
 * @param {boolean} isDrayNow - Whether the host is DrayNow
 * @returns {string[]} Array of overview category keys
 */
export const getOverviewCategories = (isDrayNow = false) => {
  return isDrayNow
    ? DRAYNOW_OVERVIEW_CATEGORIES
    : MODAL_VIEW_OVERVIEW_CATEGORIES
}

/**
 * Categories whose tables show the Chassis column, per theme.
 *
 * ModalView: tendered / in_progress / finished. DrayNow adds closed and titles
 * `tendered` "Driver Assigned" (see DRAYNOW_FILTER_META). Consumed by both list
 * logic hooks and passed to getOrdersColumns / getTripsColumns as
 * `options.chassisCategories`.
 */
export const DRAYNOW_CHASSIS_CATEGORIES = [
  'tendered',
  'in_progress',
  'finished',
  'closed',
]

export const MODAL_VIEW_CHASSIS_CATEGORIES = [
  'tendered',
  'in_progress',
  'finished',
]

/**
 * Get the categories that show the Chassis column for the current theme
 * @param {boolean} isDrayNow - Whether the host is DrayNow
 * @returns {string[]} Array of category keys
 */
export const getChassisCategories = (isDrayNow = false) => {
  return isDrayNow ? DRAYNOW_CHASSIS_CATEGORIES : MODAL_VIEW_CHASSIS_CATEGORIES
}

/**
 * Format filter title for page header
 * @param {Object} filterMeta - Filter metadata object
 * @param {string} filter - Current filter key
 * @param {string} listType - 'orders' or 'trips'
 * @returns {string} Formatted title
 */
export const formatFilterTitle = (filterMeta, filter, listType) => {
  const meta = filterMeta?.[filter]
  const filterTitle = meta?.title
  if (!filterTitle) return 'Marketplace'

  switch (filterTitle) {
    case 'Alerts':
      return `Alert ${listType}`
    case 'Overview':
      return `${listType} Overview`
    default:
      return `${filterTitle} ${listType}`
  }
}
